import { useEffect, useRef } from 'react';
import { usePrevious } from '@frameio/vapor';
import { FilterTypeTrackingMap } from '../../shared/constants';
import {
  useSearchSession,
  useTrackSearchNoResults,
  useTrackSearchResultsDisplayed,
} from '../../shared/contexts/SearchAnalyticsContext';
import type { TrackSearchScope } from '../../shared/contexts/SearchAnalyticsContext/types';
import { getTabSelected } from '../../shared/contexts/SearchAnalyticsContext/utils';
import type { SectionId } from '../../shared/types';

type UseQuickSearchResultsAnalyticsParams = {
  hasSearchQuery: boolean;
  searchQuery: string;
  hasQueriesSettled: boolean;
  standardResultsCount: number;
  mediaIntelligenceResultsCount?: number;
  searchScope?: TrackSearchScope;
  activeTab?: SectionId | null;
};

export const useQuickSearchResultsAnalytics = ({
  hasSearchQuery,
  searchQuery,
  hasQueriesSettled,
  standardResultsCount,
  mediaIntelligenceResultsCount,
  searchScope,
  activeTab,
}: UseQuickSearchResultsAnalyticsParams) => {
  const trackSearchResultsDisplayed = useTrackSearchResultsDisplayed();
  const trackSearchNoResults = useTrackSearchNoResults();
  const { setSearchResultsDisplayedAt } = useSearchSession();
  // Create a unique key for the current search to track results and no-results events.
  const searchTrackingKey = hasSearchQuery
    ? `${searchQuery}:${searchScope ?? ''}`
    : '';
  const prevSearchTrackingKey = usePrevious(searchTrackingKey);

  // Track whether we've already fired the event for this search query/scope state.
  const hasFiredEvent = useRef<boolean>(false);

  // Reset fired state when the tracked search context changes or is cleared.
  useEffect(() => {
    if (!hasSearchQuery || searchTrackingKey !== prevSearchTrackingKey) {
      hasFiredEvent.current = false;
    }
  }, [hasSearchQuery, searchTrackingKey, prevSearchTrackingKey]);

  // Fire the event when all queries have settled (only once per search)
  useEffect(() => {
    // Skip if no query, queries haven't settled, or already fired for this search
    if (!hasSearchQuery || !hasQueriesSettled || hasFiredEvent.current) return;

    const hasStandardResults = standardResultsCount > 0;
    const hasMediaIntelligenceResults = Boolean(
      mediaIntelligenceResultsCount && mediaIntelligenceResultsCount > 0
    );
    const hasAnyResults = hasStandardResults || hasMediaIntelligenceResults;

    const tabSelected = getTabSelected(activeTab);
    const filterApplied =
      searchScope === 'CurrentProject'
        ? [FilterTypeTrackingMap.LOCATION_FILTER]
        : [FilterTypeTrackingMap.NONE];

    hasFiredEvent.current = true;

    if (hasAnyResults) {
      setSearchResultsDisplayedAt(performance.now());
      trackSearchResultsDisplayed({
        filterApplied,
        isMediaIntelligenceResultsDisplayed: hasMediaIntelligenceResults,
        isStandardResultsDisplayed: hasStandardResults,
        searchQuery,
        searchScope,
        tabSelected,
      });
    } else {
      trackSearchNoResults({
        filterApplied,
        searchQuery,
        searchScope,
        webSource: 'Modal',
      });
    }
  }, [
    hasQueriesSettled,
    hasSearchQuery,
    standardResultsCount,
    mediaIntelligenceResultsCount,
    searchScope,
    searchQuery,
    activeTab,
    setSearchResultsDisplayedAt,
    trackSearchNoResults,
    trackSearchResultsDisplayed,
  ]);
};
